import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
} from "@mui/material";
import { defaultStyle } from "components/Modals";
import { Battery } from "api/models/Battery";

interface AddBatteryModalProps {
  allBatteries: Battery[];
  selectedBatteryIds: number[];
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  onAddBatteries: (batteryIds: number[]) => void;
}

const AddBatteryModal = ({
  allBatteries,
  selectedBatteryIds,
  setOpenModal,
  onAddBatteries,
}: AddBatteryModalProps) => {
  const [newlySelectedBatteries, setNewlySelectedBatteries] = useState<
    number[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter out batteries that are already in this group or in another group
  const availableBatteries = useMemo(() => {
    const filtered = allBatteries.filter(
      (battery) =>
        !selectedBatteryIds.includes(battery.id) && !battery.batteryGroupId,
    );

    if (!searchTerm) return filtered;

    const lowerSearch = searchTerm.toLowerCase();
    return filtered.filter(
      (battery) =>
        battery.productionParkName?.toLowerCase().includes(lowerSearch) ||
        battery.externalId?.toLowerCase().includes(lowerSearch) ||
        battery.id?.toString().includes(lowerSearch),
    );
  }, [allBatteries, selectedBatteryIds, searchTerm]);

  const handleToggleBattery = (batteryId: number) => {
    setNewlySelectedBatteries((prev) => {
      if (prev.includes(batteryId)) {
        return prev.filter((id) => id !== batteryId);
      } else {
        return [...prev, batteryId];
      }
    });
  };

  const handleSubmit = () => {
    if (newlySelectedBatteries.length > 0) {
      onAddBatteries(newlySelectedBatteries);
    }
  };

  return (
    <Box sx={{ ...defaultStyle, maxWidth: "800px", width: "100%" }}>
      <Typography id="modal-modal-title" variant="h5" component="h5" mb={2}>
        Add batteries to group
      </Typography>

      {allBatteries.filter(
        (battery) =>
          !selectedBatteryIds.includes(battery.id) && !battery.batteryGroupId,
      ).length === 0 ? (
        <Alert severity="info">
          No available batteries. All batteries are either already in this group
          or assigned to other groups.
        </Alert>
      ) : (
        <>
          <TextField
            size="small"
            placeholder="Search by production park, external ID, or battery ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          {availableBatteries.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No batteries found matching your search.
            </Alert>
          ) : (
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              Showing {availableBatteries.length} available{" "}
              {availableBatteries.length === 1 ? "battery" : "batteries"}
            </Typography>
          )}
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Production Park</TableCell>
                  <TableCell>External ID</TableCell>
                  <TableCell>Capacity (kWh)</TableCell>
                  <TableCell>Inverter Capacity (kW)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableBatteries.map((battery) => (
                  <TableRow
                    key={battery.id}
                    hover
                    onClick={() => handleToggleBattery(battery.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={newlySelectedBatteries.includes(battery.id)}
                        onChange={() => handleToggleBattery(battery.id)}
                      />
                    </TableCell>
                    <TableCell>{battery.id}</TableCell>
                    <TableCell>{battery.productionParkName}</TableCell>
                    <TableCell>{battery.externalId || "-"}</TableCell>
                    <TableCell>{battery.capacity}</TableCell>
                    <TableCell>{battery.inverterCapacity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={newlySelectedBatteries.length === 0}
            >
              Add {newlySelectedBatteries.length} batter
              {newlySelectedBatteries.length === 1 ? "y" : "ies"}
            </Button>
            <Button variant="outlined" onClick={() => setOpenModal(false)}>
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AddBatteryModal;
